import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Card,
  CardContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Chip,
  Avatar,
  Divider
} from '@mui/material';
import {
  Print as PrintIcon,
  GetApp as DownloadIcon,
  Search as SearchIcon,
  Science as MicroIcon,
  Visibility as ViewIcon,
  CheckCircle as PassIcon,
  Cancel as FailIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { useQuery } from 'react-query';
import { useSnackbar } from 'notistack';
import { useAuth } from '../../contexts/AuthContext';

const MicrostructureReport = () => {
  const [filters, setFilters] = useState({
    from_date: new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0],
    to_date: new Date().toISOString().split('T')[0],
    die_no: '',
    steel_code: '',
    ht_code: '',
    grain_size_range: 'all',
    test_result: 'all'
  });

  const { enqueueSnackbar } = useSnackbar();
  const { api } = useAuth();

  const { data: reportData, isLoading, error, refetch } = useQuery(
    ['microstructureReport', filters],
    async () => {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const response = await api.get(`/reports/microstructure?${params}`);
      return response.data;
    },
    {
      onError: (error) => {
        enqueueSnackbar(`Error fetching microstructure report: ${error.message}`, { variant: 'error' });
      },
      enabled: false
    }
  );

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = () => {
    enqueueSnackbar('Download functionality will be implemented', { variant: 'info' });
  };

  const handleGenerateReport = () => {
    refetch();
  };

  const getGrainSizeColor = (grainSize) => {
    if (grainSize >= 5 && grainSize <= 8) return 'success';
    if (grainSize >= 3 && grainSize <= 10) return 'warning';
    return 'error';
  };

  const getResultColor = (result) => {
    switch (result) {
      case 'pass': return 'success';
      case 'fail': return 'error';
      case 'marginal': return 'warning';
      default: return 'default';
    }
  };

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Error loading microstructure report: {error.message}
        </Alert>
      </Box>
    );
  }

  const mockData = [
    {
      test_id: 'MICRO/24/001',
      test_date: '2024-11-20',
      die_no: 'D12345',
      part_name: 'Connecting Rod',
      steel_code: 'EN24',
      ht_code: 'H',
      ht_date: '2024-11-18',
      sample_location: 'Core',
      grain_size_astm: 6,
      grain_size_spec: { min: 5, max: 8 },
      microstructure_observed: 'Tempered Martensite',
      microstructure_expected: 'Tempered Martensite',
      inclusion_rating: 'A1 T1',
      inclusion_spec: 'Max A2 T2',
      pearlite_percentage: null,
      ferrite_percentage: null,
      bainite_percentage: 85,
      martensite_percentage: 15,
      test_result: 'pass',
      remarks: 'Uniform microstructure, good tempering',
      tested_by: 'Metallurgy Lab',
      approved_by: 'Chief Metallurgist'
    },
    {
      test_id: 'MICRO/24/002',
      test_date: '2024-11-22',
      die_no: 'D67890',
      part_name: 'Crankshaft',
      steel_code: 'EN8',
      ht_code: 'N',
      ht_date: '2024-11-20',
      sample_location: 'Surface',
      grain_size_astm: 7,
      grain_size_spec: { min: 6, max: 9 },
      microstructure_observed: 'Ferrite + Pearlite',
      microstructure_expected: 'Ferrite + Pearlite',
      inclusion_rating: 'A1 T0',
      inclusion_spec: 'Max A2 T1',
      pearlite_percentage: 45,
      ferrite_percentage: 55,
      bainite_percentage: null,
      martensite_percentage: null,
      test_result: 'pass',
      remarks: 'Normal normalized microstructure',
      tested_by: 'QC Lab',
      approved_by: 'QC Manager'
    },
    {
      test_id: 'MICRO/24/003',
      test_date: '2024-11-25',
      die_no: 'D11111',
      part_name: 'Gear Blank',
      steel_code: '20MnCr5',
      ht_code: 'C',
      ht_date: '2024-11-23',
      sample_location: 'Case',
      grain_size_astm: 9,
      grain_size_spec: { min: 8, max: 11 },
      microstructure_observed: 'Martensite + Retained Austenite',
      microstructure_expected: 'Martensite',
      inclusion_rating: 'A2 T1',
      inclusion_spec: 'Max A2 T2',
      pearlite_percentage: null,
      ferrite_percentage: null,
      bainite_percentage: null,
      martensite_percentage: 92,
      test_result: 'marginal',
      remarks: 'Slight retained austenite observed',
      tested_by: 'Metallurgy Lab',
      approved_by: 'Chief Metallurgist'
    },
    {
      test_id: 'MICRO/24/004',
      test_date: '2024-11-28',
      die_no: 'D22222',
      part_name: 'Valve Component',
      steel_code: '4140',
      ht_code: 'H+T',
      ht_date: '2024-11-26',
      sample_location: 'Core',
      grain_size_astm: 4,
      grain_size_spec: { min: 5, max: 8 },
      microstructure_observed: 'Coarse Tempered Martensite',
      microstructure_expected: 'Fine Tempered Martensite',
      inclusion_rating: 'A1 T2',
      inclusion_spec: 'Max A2 T1',
      pearlite_percentage: null,
      ferrite_percentage: null,
      bainite_percentage: 78,
      martensite_percentage: 22,
      test_result: 'fail',
      remarks: 'Coarse grain size, inclusion level high',
      tested_by: 'QC Lab',
      approved_by: 'QC Manager'
    }
  ];

  const totalTests = mockData.length;
  const passedTests = mockData.filter(item => item.test_result === 'pass').length;
  const failedTests = mockData.filter(item => item.test_result === 'fail').length;
  const marginalTests = mockData.filter(item => item.test_result === 'marginal').length;

  return (
    <Box p={3}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <MicroIcon sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
        <Typography variant="h4" gutterBottom color="primary">
          Microstructure Analysis Report
        </Typography>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Report Filters</Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="From Date"
              InputLabelProps={{ shrink: true }}
              value={filters.from_date}
              onChange={(e) => setFilters({ ...filters, from_date: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="To Date"
              InputLabelProps={{ shrink: true }}
              value={filters.to_date}
              onChange={(e) => setFilters({ ...filters, to_date: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Die Number"
              value={filters.die_no}
              onChange={(e) => setFilters({ ...filters, die_no: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Steel Code"
              value={filters.steel_code}
              onChange={(e) => setFilters({ ...filters, steel_code: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Test Result</InputLabel>
              <Select
                value={filters.test_result}
                label="Test Result"
                onChange={(e) => setFilters({ ...filters, test_result: e.target.value })}
              >
                <MenuItem value="all">All Results</MenuItem>
                <MenuItem value="pass">Pass</MenuItem>
                <MenuItem value="fail">Fail</MenuItem>
                <MenuItem value="marginal">Marginal</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              onClick={handleGenerateReport}
              startIcon={<SearchIcon />}
            >
              Generate
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Action Buttons */}
      <Box mb={3} display="flex" gap={2}>
        <Button
          variant="outlined"
          startIcon={<PrintIcon />}
          onClick={handlePrint}
        >
          Print
        </Button>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={handleDownload}
        >
          Export to Excel
        </Button>
      </Box>

      {isLoading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" color="primary">
                    {totalTests}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Total Tests
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" color="success.main">
                    {passedTests}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Passed
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" color="warning.main">
                    {marginalTests}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Marginal
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" color="error.main">
                    {failedTests}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Failed
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Microstructure Test Table */}
          <Paper>
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="h6">
                Microstructure Test Results
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Period: {filters.from_date} to {filters.to_date}
              </Typography>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Test Details</strong></TableCell>
                    <TableCell><strong>Part Information</strong></TableCell>
                    <TableCell><strong>Heat Treatment</strong></TableCell>
                    <TableCell align="center"><strong>Grain Size (ASTM)</strong></TableCell>
                    <TableCell><strong>Microstructure</strong></TableCell>
                    <TableCell><strong>Phase Analysis</strong></TableCell>
                    <TableCell align="center"><strong>Inclusions</strong></TableCell>
                    <TableCell align="center"><strong>Result</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {mockData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        No microstructure test data found for the selected criteria
                      </TableCell>
                    </TableRow>
                  ) : (
                    mockData.map((record, index) => (
                      <TableRow key={index} hover>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="bold" color="primary">
                              {record.test_id}
                            </Typography>
                            <Typography variant="caption" display="block">
                              Date: {new Date(record.test_date).toLocaleDateString()}
                            </Typography>
                            <Typography variant="caption" display="block" color="textSecondary">
                              Location: {record.sample_location}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {record.part_name}
                            </Typography>
                            <Typography variant="caption" display="block">
                              Die: {record.die_no}
                            </Typography>
                            <Chip 
                              label={record.steel_code} 
                              color="primary" 
                              variant="outlined" 
                              size="small"
                              sx={{ mt: 0.5 }}
                            />
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Chip 
                              label={record.ht_code} 
                              color="secondary" 
                              size="small"
                              sx={{ mb: 0.5 }}
                            />
                            <Typography variant="caption" display="block">
                              HT Date: {new Date(record.ht_date).toLocaleDateString()}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Box>
                            <Avatar 
                              sx={{ 
                                width: 32, 
                                height: 32, 
                                bgcolor: getGrainSizeColor(record.grain_size_astm),
                                mx: 'auto',
                                mb: 0.5
                              }}
                            >
                              {record.grain_size_astm}
                            </Avatar>
                            <Typography variant="caption" display="block">
                              Spec: {record.grain_size_spec.min}-{record.grain_size_spec.max}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {record.microstructure_observed}
                            </Typography>
                            <Typography variant="caption" display="block" color="textSecondary">
                              Expected: {record.microstructure_expected}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box>
                            {record.ferrite_percentage && (
                              <Typography variant="caption" display="block">
                                Ferrite: {record.ferrite_percentage}%
                              </Typography>
                            )}
                            {record.pearlite_percentage && (
                              <Typography variant="caption" display="block">
                                Pearlite: {record.pearlite_percentage}%
                              </Typography>
                            )}
                            {record.bainite_percentage && (
                              <Typography variant="caption" display="block">
                                Bainite: {record.bainite_percentage}%
                              </Typography>
                            )}
                            {record.martensite_percentage && (
                              <Typography variant="caption" display="block">
                                Martensite: {record.martensite_percentage}%
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {record.inclusion_rating}
                            </Typography>
                            <Typography variant="caption" display="block" color="textSecondary">
                              Max: {record.inclusion_spec}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Box>
                            <Chip 
                              label={record.test_result.toUpperCase()}
                              color={getResultColor(record.test_result)}
                              size="small"
                              icon={
                                record.test_result === 'pass' ? <PassIcon /> :
                                record.test_result === 'fail' ? <FailIcon /> : <WarningIcon />
                              }
                              sx={{ mb: 0.5 }}
                            />
                            <Typography variant="caption" display="block" color="textSecondary">
                              {record.tested_by}
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* Microstructure Guide */}
          <Paper sx={{ mt: 3, p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Microstructure Analysis Guide
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="subtitle2" color="primary">ASTM Grain Size:</Typography>
                <Typography variant="body2">
                  • Finer grain size = Higher number
                  • Typical range: 5-8 for most parts
                  • Affects strength and toughness
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="subtitle2" color="secondary.main">Common Microstructures:</Typography>
                <Typography variant="body2">
                  • Ferrite + Pearlite (Normalized/Annealed)
                  • Tempered Martensite (Hardened + Tempered)
                  • Bainite (Isothermal treatment)
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="subtitle2" color="info.main">Inclusion Rating:</Typography>
                <Typography variant="body2">
                  • A = Sulfide inclusions
                  • T = Oxide inclusions  
                  • Number = severity (0-5 scale)
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </>
      )}
    </Box>
  );
};

export default MicrostructureReport;